import {
  getVesselSearchData,
  getCompliancePortSearchData,
  getLoadPortSearchData,
  getVoyageSearchData,
  updateEmailStartedTime,
  updateTaskStartedTime,
  updateComplinaceRejectedTask,
  updateComplinaceRejectedTaskByCompliance,
  updateComplinaceTaskApproval,
  updateVesselReference,
  refreshBol
} from "@/common/service/apiaggregator/apiAgreegatorService";
import {
  getManifestDetails,
  getManifestSearchData,
  getVesselReferences,
  updateCustomReferences,
  updateSubmittedManifest,
  getManifestPageCount,
  unlinkManifestAndConsignment,
  updateFrozenETA
} from "@/common/service/apiaggregator/manifestAgreegatorService";
import { toLuxonDate } from "@/common/utils/generic/dateHelper.js";
import { convertToLowerCase, convertToSortedData } from "@/common/utils/generic/commonHelper.js";
import { constants } from "@/lib/CONSTANTS.js";

const removeDuplicateObject = data => {
  const portData = [];
  data.forEach(element => {
    let tempItem = { label: "", value: "", name: "", stateName: "" };
    tempItem.label = element.cityName;
    tempItem.value = element.countryName;
    tempItem.name = state.typeSelected !== "Import Rail Manifest" ? element.cityCode : element.siteCode;
    tempItem.stateName = state.typeSelected !== "Import Rail Manifest" ? element.stateName : element.siteCode;

    portData.push(tempItem);
  });
  const filteredPortData = Array.from(portData.reduce((map, obj) => map.set(obj.name, obj), new Map()).values());
  const dataLoadCompPort = [];
  filteredPortData.forEach(element => {
    let displayPortDetails = element.stateName
      ? element.label + " (" + element.stateName + ")" + ", " + element.value
      : element.label + ", " + element.value;
    let item = { label: "", value: "", name: "" };
    item.label = item.value = displayPortDetails;
    item.name = element.name;
    dataLoadCompPort.push(item);
  });
  return dataLoadCompPort;
};

const changeFormat = i => {
  if (state.dateRangeSelected[i] !== null) {
    const { day, month, year } = toLuxonDate(state.dateRangeSelected[i]).c;
    return `${day}/${month}/${year}`;
  }
};

const getDetletedManifestValue = (country, manifestType, list) => {
  const countryList = list.find(obj => {
    return convertToLowerCase(obj.value) === convertToLowerCase(manifestType);
  });
  const manifestObject = countryList.countries.find(obj => {
    return obj.name === country;
  });

  if (manifestObject && manifestObject.deleteManifest) {
    return true;
  } else {
    return false;
  }
};

const getConsignmentValue = (country, manifestType, list) => {
  const countryList = list.find(obj => {
    return convertToLowerCase(obj.value) === convertToLowerCase(manifestType);
  });

  let manifestObject = countryList
    ? countryList.countries.find(obj => {
        return obj.name === country;
      })
    : "BOL";

  return manifestObject && manifestObject.valueMap && manifestObject.valueMap.consignmentType
    ? manifestObject.valueMap.consignmentType
    : "BOL";
};

const normalizeExemptionFlags = row => {
  if (!row || typeof row !== "object") {
    return row;
  }

  const normalizedRow = {
    ...row,
    isSocExempted: row.isSocExempted === true || row.socExempted === true,
    socExempted: row.socExempted === true || row.isSocExempted === true,
    isMllFilingExempt: row.isMllFilingExempt === true || row.mllFilingExemptionRequired === true,
    mllFilingExemptionRequired: row.mllFilingExemptionRequired === true || row.isMllFilingExempt === true
  };

  if (Array.isArray(row.houseBols)) {
    normalizedRow.houseBols = row.houseBols.map(normalizeExemptionFlags);
  }

  return normalizedRow;
};

const filterExemptedRows = rows => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .map(normalizeExemptionFlags)
    .filter(
      row =>
        row?.isSocExempted === true ||
        row?.socExempted === true ||
        row?.isMllFilingExempt === true ||
        row?.mllFilingExemptionRequired === true
    );
};

const getRowDedupKey = row => {
  if (!row || typeof row !== "object") {
    return "";
  }
  return row.consignmentNumber || row.bolNumber || row.systemHblNumber || "";
};

const mergeExemptionRows = (firstRows = [], secondRows = []) => {
  const mergedMap = new Map();

  [...firstRows, ...secondRows].forEach(row => {
    const normalizedRow = normalizeExemptionFlags(row);
    const key = getRowDedupKey(normalizedRow);

    if (!key) {
      return;
    }

    if (!mergedMap.has(key)) {
      mergedMap.set(key, normalizedRow);
      return;
    }

    const existingRow = mergedMap.get(key);
    const mergedRow = normalizeExemptionFlags({
      ...existingRow,
      ...normalizedRow
    });

    const existingHouseBols = Array.isArray(existingRow.houseBols) ? existingRow.houseBols : [];
    const incomingHouseBols = Array.isArray(normalizedRow.houseBols) ? normalizedRow.houseBols : [];
    if (existingHouseBols.length > 0 || incomingHouseBols.length > 0) {
      mergedRow.houseBols = mergeExemptionRows(existingHouseBols, incomingHouseBols);
    }

    mergedMap.set(key, mergedRow);
  });

  return Array.from(mergedMap.values());
};

const toArrayPayload = payload => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === "object") {
    return [payload];
  }
  return [];
};

const parseJsonSafely = async response => {
  if (!response || typeof response.json !== "function") {
    return [];
  }
  try {
    return await response.json();
  } catch (error) {
    return [];
  }
};

/**
 * Helper function to format cargo type values for API request
 * Matches the behavior of bolType filter for consistency
 * @param {Array} cargoTypeList - Array of selected cargo types
 * @returns {string} Formatted cargo type string for API (empty string or comma-separated values)
 */
const getCargoTypeParam = cargoTypeList => {
  return cargoTypeList?.toString() || "";
};

/**
 * Helper function to filter results based on Empty Bill selection.
 * Server always returns empty bills regardless of bolType filter, so we handle empty bill
 * inclusion/exclusion entirely on the client side. Laden bill type filtering is left to the server.
 * - "Empty Bill" only → show only empty bills
 * - "Empty Bill" + other types → return all (server filtered laden bills; keep empty bills included)
 * - Other types only (no "Empty Bill") → exclude empty bills (server doesn't filter them out)
 * @param {Array} data - Array of compliance details
 * @param {Array} bolTypeListValues - Array of selected BOL types
 * @returns {Array} Filtered data based on empty bill selection
 */
const filterByEmptyBill = (data, bolTypeListValues) => {
  if (
    !Array.isArray(data) ||
    data.length === 0 ||
    !Array.isArray(bolTypeListValues) ||
    bolTypeListValues.length === 0
  ) {
    return data;
  }

  const hasEmptyBillFilter = bolTypeListValues.includes("Empty Bill");
  const hasOtherBolTypes = bolTypeListValues.some(type => type !== "Empty Bill");
  const isEmptyBill = item => item.empty === true || item.empty === "true";

  // Only "Empty Bill" selected → show only empty bills
  if (hasEmptyBillFilter && !hasOtherBolTypes) {
    return data.filter(item => isEmptyBill(item));
  }

  // "Empty Bill" + other types selected → server already filtered laden bills by type
  // Just return all data so empty bills are included alongside the server-filtered laden bills
  if (hasEmptyBillFilter && hasOtherBolTypes) {
    return data;
  }

  // "Empty Bill" not selected → exclude empty bills (server doesn't filter them out)
  return data.filter(item => !isEmptyBill(item));
};

const state = {
  vesselList: [],
  voyageList: [],
  vesselListLoader: false,
  loadPortList: [],
  loadPortListLoader: false,
  compliancePortList: [],
  complianceDetailsList: [],
  complianceContainerList: [],
  vesselNotificationDetailsList: [],
  complianceDetailsListLoader: false,
  complianceDetailsContainerListLoader: false,
  consignmentReferenceDetailLoader: false,
  vesselNotificationDetailsListLoader: false,
  complianceListLoader: false,
  typeSelected: "",
  countrySelected: "",
  deadlineSelected: "",
  dateRangeSelected: {},
  compliancePortSelected: "",
  loadPortSelected: "",
  vesselCodeSelected: "",
  vesselNameSelected: "",
  vesselVoyageSelected: "",
  voyageListLoader: false,
  updateVesselLoader: false,
  emailDesc: "",
  taskDesc: "",
  bsKey: "",
  tskBsKey: "",
  compResult: [],
  compResultLoader: false,
  isManuallyTriggered: false,
  compResultError: null,
  updateVesselError: null,
  complainceDetailsRecordsPage: 1,
  complainceDetailsRecordsPerPage: constants.pageSettings.manifest.searchResultsPerPage,
  pgCount: 0,
  compId: "",
  manifestSub: [],
  manualSub: [],
  pRowIndex: null,
  rejectedRowIndex: null,
  pFlag: false,
  emailVoyage: "",
  emailVesselName: "",
  manualRejSub: [],
  manualRejSingleSub: [],
  compPort: "",
  loadPort: "",
  complianceType: "",
  currentGsisKey: "",
  filterGrpValues: [],
  statusListValues: [],
  customStatusListValues: [],
  bolTypeListValues: [],
  cargoTypeListValues: [],
  moveTypeListValues: [],
  stageListValues: [],
  okToLoadListValues: [],
  selectedBolList: [],
  deliveryPlacesValues: [],
  customNotificationStatus: [],
  selectedAllBol: false,
  bolSearchNo: "",
  vanVSAPartnerList: constants.pageSettings.vanVSAPartner,
  showContainerBrokenDownMessage: false,
  showExemptionsOnly: false,
  seaTypeSelected: "ALL" // Options: "ALL", "SHORT_SEA", "DEEP_SEA"
};

const filterBySeaType = (data, seaType) => {
  if (seaType === "SHORT_SEA")
    return data.filter(item => item.shortSeaDeadLineTimeStamp !== null && item.shortSeaDeadLineTimeStamp !== "");
  if (seaType === "DEEP_SEA")
    return data.filter(item => item.shortSeaDeadLineTimeStamp === null || item.shortSeaDeadLineTimeStamp === "");
  return data;
};

const getters = {
  getFilterModuleDetails: state => {
    const { typeSelected, vesselNameSelected, vesselCodeSelected, vesselVoyageSelected } = state;
    return { typeSelected, vesselNameSelected, vesselCodeSelected, vesselVoyageSelected };
  },
  getFilteredManifestResults: state => filterBySeaType(state.compResult, state.seaTypeSelected),
  getSeaTypeCounts: state => ({
    all: state.compResult.length,
    shortSea: filterBySeaType(state.compResult, "SHORT_SEA").length,
    deepSea: filterBySeaType(state.compResult, "DEEP_SEA").length
  })
};

const mutations = {
  setSelectedBolList(state, data) {
    state.selectedBolList = data;
  },
  setSelectedAllBol(state, data) {
    state.selectedAllBol = data;
  },
  ammendSelectedBolList(state, data) {
    let itemIndex = state.selectedBolList.findIndex(x => x == data);
    if (itemIndex < 0) {
      state.selectedBolList.push(data);
    } else {
      state.selectedBolList.splice(itemIndex, 1);
    }
  },
  setCurrentGsisKey(state, data) {
    state.currentGsisKey = data;
  },
  setStatusListValues(state, data) {
    state.statusListValues = data;
  },
  setCustomStatusListValues(state, data) {
    state.customStatusListValues = data;
  },
  setBolTypeListValues(state, data) {
    state.bolTypeListValues = data;
  },
  setCargoTypeListValues(state, data) {
    state.cargoTypeListValues = data;
  },
  setMoveTypeListValues(state, data) {
    state.moveTypeListValues = data;
  },
  setIsManuallyTriggered(state, data) {
    state.isManuallyTriggered = data;
  },
  setOkToLoadListValues(state, data) {
    state.okToLoadListValues = data;
  },
  setDeliveryPlacesValues(state, data) {
    state.deliveryPlacesValues = data;
  },
  setCustomNotificationStatus(state, data) {
    state.customNotificationStatus = data;
  },
  setSeaTypeSelected(state, value) {
    state.seaTypeSelected = value;
  },
  setShowExemptionsOnly(state, data) {
    state.showExemptionsOnly = data;
  },
  setStageListValues(state, data) {
    state.stageListValues = data;
  },
  setBOLSearchNo(state, data) {
    state.bolSearchNo = data;
  },
  setBolStageList(state, data) {
    state.bolStageList = data;
  },
  setVesselList(state, result) {
    state.vesselList = result;
  },
  setLoadPortList(state, result) {
    state.loadPortList = result;
  },
  setCompliancePortList(state, result) {
    state.compliancePortList = result;
  },
  setComplianceDetailsList(state, result) {
    state.complianceDetailsList = result;
  },
  setComplianceContainerList(state, result) {
    state.complianceContainerList = result;
  },
  setContainerBrokenDownMessageDisplay(state, result) {
    state.showContainerBrokenDownMessage = result;
  },
  setVesselNotificationDetailsList(state, result) {
    state.vesselNotificationDetailsList = result;
  },
  setVoyageList(state, result) {
    state.voyageList = result;
  },
  setEmailDesc(state, result) {
    state.emailDesc = result;
  },
  setTaskDesc(state, result) {
    state.taskDesc = result;
  },
  setBusKey(state, result) {
    state.bsKey = result;
  },
  setTskBusKey(state, result) {
    state.tskBsKey = result;
  },
  setErrorState(state, param) {
    state[`${param.type}Error`] = param.error
      ? "hasError" in param.error
        ? param.error
        : { hasError: true, status: param.error.status, message: param.error.message }
      : null;
  },
  setLoadingState(state, param) {
    state[`${param.type}Loader`] = param.value;
  },
  setUserSelection(state, param) {
    const { type, value } = param;
    state[`${type}Selected`] = value;
  },
  setCompResult(state, result) {
    state.compResult = result;
  },
  setPgCount(state, result) {
    state.pgCount = result;
  },
  setManifestSearchCurrentPage(state, value) {
    state.complainceDetailsRecordsPage = value;
  },
  setManifestPageSize(state, value) {
    state.complainceDetailsRecordsPerPage = value;
  },
  setComplianceId(state, value) {
    state.compId = value;
  },
  setManifestSubmission(state, result) {
    state.manifestSub = result;
  },
  setManualStatus(state, result) {
    state.manualSub = result;
  },
  setPRowIndex(state, index) {
    state.pRowIndex = index;
  },
  setRejectedRowIndex(state, index) {
    state.rejectedRowIndex = index;
  },
  setPaginationFlag(state, value) {
    state.pFlag = value;
  },
  setEmailVoyage(state, value) {
    state.emailVoyage = value;
  },
  setEmailVesselName(state, value) {
    state.emailVesselName = value;
  },
  setManualRejSubStatus(state, value) {
    state.manualRejSub = value;
  },
  setManualRejSingleSubStatus(state, value) {
    state.manualRejSingleSub = value;
  },
  setCompliancePort(state, value) {
    state.compPort = value;
  },
  setLoadPort(state, value) {
    state.loadPort = value;
  },
  setComplianceType(state, value) {
    state.complianceType = value;
  },
  setFilters(state, value) {
    state.filterGrpValues = value;
  }
};
const actions = {
  async fetchVesselList({ commit }, params) {
    commit("setVesselList", []);
    commit("setLoadingState", { type: "vesselList", value: true });
    try {
      let rawData,
        data = [];
      rawData = await getVesselSearchData(params, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "vesselList",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      commit("setVesselList", data);
      commit("setErrorState", { type: "vesselList", error: "" });
      commit("setLoadingState", { type: "vesselList", value: false });
    } catch (error) {
      commit("setVesselList", []);
      commit("setErrorState", { type: "vesselList", error });
      commit("setLoadingState", { type: "vesselList", value: false });
    }
  },
  async fetchCompliancePortList({ commit }, countryObj) {
    commit("setCompliancePortList", []);
    commit("setSelectedBolList", []);
    commit("setSelectedAllBol", false);
    commit("setLoadingState", { type: "complianceList", value: true });
    try {
      // const { search, countryCode } = params;
      let rawData,
        data = [];
      commit("setCompResult", []);
      rawData = await getCompliancePortSearchData(countryObj, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "compliancePortList",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      const compliancePortData = removeDuplicateObject(data);

      commit("setCompliancePortList", compliancePortData);
      commit("setErrorState", { type: "compliancePortList", error: "" });
      commit("setLoadingState", { type: "complianceList", value: false });
    } catch (error) {
      commit("setCompliancePortList", []);
      commit("setErrorState", { type: "compliancePortList", error });
      commit("setLoadingState", { type: "complianceList", value: false });
    }
  },
  async fetchLoadPortList({ commit }, params) {
    commit("setLoadPortList", []);
    commit("setLoadingState", { type: "loadPortList", value: true });
    try {
      let { search, loadCountry, searchType = "loadPort" } = params;

      // For Rail Manifest, we need below value
      if (state.typeSelected === "Import Rail Manifest") {
        loadCountry = "CA";
        searchType = "all";
      }
      let rawData,
        data = [];

      rawData = await getLoadPortSearchData(search, loadCountry, searchType, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "loadPortList",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      const loadPortData = removeDuplicateObject(data);

      commit("setLoadPortList", loadPortData);
      commit("setErrorState", { type: "loadPortList", error: "" });
      commit("setLoadingState", { type: "loadPortList", value: false });
    } catch (error) {
      commit("setLoadPortList", []);
      commit("setErrorState", { type: "loadPortList", error });
      commit("setLoadingState", { type: "loadPortList", value: false });
    }
  },
  setSeaTypeFilter({ commit }, seaType) {
    commit("setSeaTypeSelected", seaType);
    commit("setManifestSearchCurrentPage", 1);
  },
  async fetchComplianceDetailsList({ commit, rootState }, newParams) {
    commit("setSelectedBolList", []);
    commit("setSelectedAllBol", false);
    commit("setComplianceDetailsList", []);

    let { compId, compPort, loadPort, complianceType, isManuallyTriggered, consignmentType, pageSize } = newParams;

    if (consignmentType == "BOL") {
      commit("setLoadingState", { type: "complianceDetailsList", value: true });
    } else {
      if (rootState.commonStore.manifestTypeCountryList && rootState.commonStore.manifestTypeCountryList.length) {
        let configuredConsignmentType = getConsignmentValue(
          state.countrySelected,
          state.typeSelected,
          rootState.commonStore.manifestTypeCountryList
        );
        consignmentType = configuredConsignmentType !== "BOL" ? configuredConsignmentType : consignmentType;
      }

      commit("setLoadingState", { type: "complianceDetailsContainerList", value: true });
    }

    try {
      let rawData,
        data = [],
        queryParams,
        deleteManifest = false;

      if (rootState.commonStore.manifestTypeCountryList && rootState.commonStore.manifestTypeCountryList.length) {
        deleteManifest = getDetletedManifestValue(
          state.countrySelected,
          state.typeSelected,
          rootState.commonStore.manifestTypeCountryList
        );
      }
      queryParams = {
        compId,
        compPort,
        clearanceLocation: state.compliancePortSelected || "",
        loadPort,
        complianceType,
        stageValue:
          state.stageListValues.toString() && state.stageListValues.toString() !== undefined
            ? state.stageListValues.toString()
            : "",
        statusValue:
          state.statusListValues.toString() && state.statusListValues.toString() !== undefined
            ? state.statusListValues.toString()
            : "",
        okToLoadValue: state.okToLoadListValues,
        cargoType: state.cargoTypeListValues?.includes("UB") ? "UB" : "",
        bolNumber: "",
        consignmentType,
        page: state.complainceDetailsRecordsPage,
        size: pageSize > 0 ? pageSize : state.complainceDetailsRecordsPerPage,
        isManuallyTriggered: isManuallyTriggered === true ? true : false,
        deleteManifest,
        sourceType: newParams.sourceType ? newParams.sourceType : "normal",
        customNotificationStatus: state.customNotificationStatus ? state.customNotificationStatus : []
      };
      rawData = await getManifestDetails(queryParams, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "complianceDetailsList",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      data = Array.isArray(data) ? data.map(normalizeExemptionFlags) : [];
      commit("setComplianceId", compId);
      commit("setCompliancePort", compPort);
      commit("setLoadPort", loadPort);
      commit("setComplianceType", complianceType);
      commit("setIsManuallyTriggered", isManuallyTriggered);
      if (consignmentType == "BOL") {
        commit("setComplianceDetailsList", data);
        commit("setLoadingState", { type: "complianceDetailsList", value: false });
      } else {
        commit("setComplianceContainerList", data);
        commit("setLoadingState", { type: "complianceDetailsContainerList", value: false });
      }
      commit("setErrorState", { type: "complianceDetailsList", error: "" });

      if (data.length > 0) {
        commit("setPaginationFlag", true);
      }
    } catch (error) {
      commit("setErrorState", { type: "complianceDetailsList", error });
      if (consignmentType == "BOL") {
        commit("setComplianceDetailsList", []);
        commit("setLoadingState", { type: "complianceDetailsList", value: false });
      } else {
        commit("setComplianceContainerList", []);
        commit("setLoadingState", { type: "complianceDetailsContainerList", value: false });
      }
    }
  },
  async fetchVesselDetailsList({ commit }, newParams) {
    commit("setSelectedBolList", []);
    commit("setSelectedAllBol", false);
    commit("setComplianceDetailsList", []);
    commit("setVesselNotificationDetailsList", []);
    commit("setLoadingState", { type: "vesselNotificationDetailsList", value: true });
    const { compId, compPort, gsisKey, complianceType } = newParams;
    try {
      let rawData,
        data = [],
        queryParams;
      queryParams = {
        gsisKey
      };
      rawData = await getVesselReferences(queryParams, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "vesselNotificationDetailsList",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      if (compId && compId.length > 0) {
        commit("setComplianceId", compId);
        commit("setCompliancePort", compPort);
        commit("setComplianceType", complianceType);
      }
      for (const mainItem of state.vanVSAPartnerList) {
        let counter = 0;
        for (const childItem of data.vesselReferences) {
          if (childItem.brand === mainItem) {
            counter++;
          }
        }
        if (counter === 0) {
          const item = {
            brand: mainItem,
            direction: "Import",
            maerskOwned: false,
            references: [
              {
                referenceName: "weight",
                referenceValue: ""
              },
              {
                referenceName: "Manifest Reference Number",
                referenceValue: ""
              }
            ]
          };
          data.vesselReferences.push(item);
        }
      }
      commit("setVesselNotificationDetailsList", data);
      commit("setCurrentGsisKey", gsisKey);
      commit("setErrorState", { type: "vesselNotificationDetailsList", error: "" });
      commit("setLoadingState", { type: "vesselNotificationDetailsList", value: false });
      if (data.length > 0) {
        commit("setPaginationFlag", true);
      }
    } catch (error) {
      commit("setComplianceDetailsList", []);
      commit("setErrorState", { type: "vesselNotificationDetailsList", error });
      commit("setLoadingState", { type: "vesselNotificationDetailsList", value: false });
    }
  },
  async clearAllFilterValues({ commit }) {
    commit("setStageListValues", []);
    commit("setStatusListValues", []);
    commit("setOkToLoadListValues", []);
    commit("setDeliveryPlacesValues", []);
    commit("setCustomStatusListValues", []);
    commit("setBolTypeListValues", []);
    commit("setCargoTypeListValues", []);
    commit("setBOLSearchNo", "");
    commit("setShowExemptionsOnly", false);
  },
  async filterComplianceDetailsList({ commit, rootState }, params) {
    let consignmentType = params.consignmentType ? params.consignmentType : "BOL";

    if (
      consignmentType !== "BOL" &&
      rootState.commonStore.manifestTypeCountryList &&
      rootState.commonStore.manifestTypeCountryList.length
    ) {
      let configuredConsignmentType = getConsignmentValue(
        state.countrySelected,
        state.typeSelected,
        rootState.commonStore.manifestTypeCountryList
      );
      consignmentType = configuredConsignmentType !== "BOL" ? configuredConsignmentType : consignmentType;
    }

    if (params && params.type && params.type != "page" && consignmentType && consignmentType == "BOL") {
      commit("setComplianceDetailsList", []);
    }

    if (params && params.type && params.type == "page" && state.showContainerBrokenDownMessage) {
      commit("setContainerBrokenDownMessageDisplay", true);
    } else {
      commit("setContainerBrokenDownMessageDisplay", false);
    }
    if (!params.isDownloadAll && !params.preserveBolSelection) {
      commit("setSelectedBolList", []);
      commit("setSelectedAllBol", false);
    }
    if (!params.isDownloadAll) {
      if (consignmentType && consignmentType == "BOL") {
        commit("setLoadingState", { type: "complianceDetailsList", value: true });
      } else {
        commit("setLoadingState", { type: "complianceDetailsContainerList", value: true });
      }
    }

    try {
      let rawData,
        data = [],
        queryParams;
      if (params && params.type && params.type === "stage") {
        commit("setStageListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "status") {
        commit("setStatusListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "customStatus") {
        commit("setCustomStatusListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "bolType") {
        commit("setBolTypeListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "cargoType") {
        commit("setCargoTypeListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "moveType") {
        commit("setMoveTypeListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "okToLoad") {
        commit("setOkToLoadListValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "bolNo") {
        commit("setStageListValues", []);
        commit("setStatusListValues", []);
        commit("setCustomStatusListValues", []);
        commit("setCustomNotificationStatus", []);
        commit("setBolTypeListValues", []);
        commit("setCargoTypeListValues", []);
        commit("setMoveTypeListValues", []);
        commit("setOkToLoadListValues", []);
        commit("setDeliveryPlacesValues", []);
        commit("setBOLSearchNo", params.bolNo);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "placeOfDelivery") {
        commit("setDeliveryPlacesValues", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "customNotificationStatus") {
        commit("setCustomNotificationStatus", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      } else if (params && params.type && params.type === "exemptionsOnly") {
        commit("setShowExemptionsOnly", params.listValue);
        commit("setManifestSearchCurrentPage", 1);
      }

      let deleteManifest = false;
      if (rootState.commonStore.manifestTypeCountryList && rootState.commonStore.manifestTypeCountryList.length) {
        deleteManifest = getDetletedManifestValue(
          state.countrySelected,
          state.typeSelected,
          rootState.commonStore.manifestTypeCountryList
        );
      }
      queryParams = {
        compId: state.compId,
        compPort: state.compPort,
        loadPort: state.loadPort,
        complianceType: state.complianceType,
        stageValue:
          state.stageListValues.toString() && state.stageListValues.toString() !== undefined
            ? state.stageListValues.toString()
            : "",
        statusValue:
          state.statusListValues.toString() && state.statusListValues.toString() !== undefined
            ? state.statusListValues.toString()
            : "",
        customsStatus: state.customStatusListValues?.toString(),
        bolType: state.bolTypeListValues?.toString(),
        cargoType: getCargoTypeParam(state.cargoTypeListValues),
        moveType: state.moveTypeListValues?.toString(),
        okToLoadValue: state.okToLoadListValues,
        bolNumber: params.bolNo ? params.bolNo : state.bolSearchNo ? state.bolSearchNo : "",
        consignmentType: consignmentType,
        page: params && params.type && params.type === "page" ? params.listValue : state.complainceDetailsRecordsPage,
        size:
          params && params.pageSize && params.pageSize > constants.pageSettings.manifest.searchResultsPerPage
            ? params.pageSize
            : constants.pageSettings.manifest.searchResultsPerPage,
        isManuallyTriggered: state.isManuallyTriggered === true ? true : false,
        deleteManifest,
        sourceType: params.sourceType ? params.sourceType : "normal",
        isBolLevelReferenceExists: params.isBolLevelReferenceExists ? params.isBolLevelReferenceExists : "",
        bda: params.bda ? params.bda : "",
        applicableReferenceTypes: params.applicableReferenceTypes ? params.applicableReferenceTypes : "",
        placeOfDelivery: state.deliveryPlacesValues ? state.deliveryPlacesValues : "",
        customNotificationStatus: state.customNotificationStatus ? state.customNotificationStatus : [],
        showExemptionsOnly: state.showExemptionsOnly ? state.showExemptionsOnly : false
      };
      if (state.showExemptionsOnly) {
        const [ensResult, socResult] = await Promise.allSettled([
          getManifestDetails({ ...queryParams, exemptionType: "ENS" }, true),
          getManifestDetails({ ...queryParams, exemptionType: "SOC" }, true)
        ]);

        let ensData = [];
        let socData = [];

        if (ensResult.status === "fulfilled") {
          ensData = await parseJsonSafely(ensResult.value);
        }

        if (socResult.status === "fulfilled") {
          socData = await parseJsonSafely(socResult.value);
        }

        data = mergeExemptionRows(toArrayPayload(ensData), toArrayPayload(socData));
        data = filterExemptedRows(data);
        // Apply Empty Bill filter client-side
        data = filterByEmptyBill(data, state.bolTypeListValues);
      } else {
        rawData = await getManifestDetails(queryParams, true);
        if (rawData.status > 200 && rawData.status <= 299) {
          data = [];
          commit("setErrorState", {
            type: "complianceDetailsList",
            error: { hasError: false, status: rawData.status, message: rawData.statusText }
          });
        } else {
          data = await rawData.json();
        }

        data = toArrayPayload(data).map(normalizeExemptionFlags);
        // Apply Empty Bill filter client-side
        data = filterByEmptyBill(data, state.bolTypeListValues);
      }

      // Update BOL count after client-side Empty Bill filtering
      if (state.bolTypeListValues.includes("Empty Bill") && consignmentType == "BOL") {
        commit("setPgCount", {
          ...state.pgCount,
          bolReferenceCount: Array.isArray(data) ? data.length : 0
        });
      }

      if (params.isDownloadAll) {
        return data;
      } else {
        if (consignmentType == "BOL") {
          commit("setComplianceDetailsList", data[0] && data[0].containerReferencesCreated ? [] : data);
          commit("setLoadingState", { type: "complianceDetailsList", value: false });
          if (params.type === "bolNo") {
            commit("setComplianceContainerList", data[0] && data[0].containerReferencesCreated ? data : []);
            commit(
              "setContainerBrokenDownMessageDisplay",
              data[0] && data[0].containerReferencesCreated ? true : false
            );
          }
        } else {
          commit("setComplianceContainerList", data);
          commit("setLoadingState", { type: "complianceDetailsContainerList", value: false });
          if (params.type === "bolNo") {
            commit("setComplianceDetailsList", []);
          }
        }
      }
      commit("setErrorState", { type: "complianceDetailsList", error: "" });
      if (data.length > 0) {
        commit("setPaginationFlag", true);
      }
    } catch (error) {
      commit("setErrorState", { type: "complianceDetailsList", error });
      if (consignmentType == "BOL") {
        commit("setComplianceDetailsList", []);
        commit("setLoadingState", { type: "complianceDetailsList", value: false });
        if (params.type === "bolNo") commit("setComplianceContainerList", []);
      } else {
        commit("setComplianceContainerList", []);
        commit("setLoadingState", { type: "complianceDetailsContainerList", value: false });
        if (params.type === "bolNo") {
          commit("setComplianceDetailsList", []);
        }
      }
    }
  },
  async voyageTypeList({ commit }, paramValues) {
    commit("setVoyageList", []);
    commit("setLoadingState", { type: "voyageList", value: true });
    try {
      let rawData,
        data = [];

      rawData = await getVoyageSearchData(paramValues, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "voyageList",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }

      commit("setVoyageList", convertToSortedData(data, "voyage"));
      commit("setErrorState", { type: "voyageList", error: "" });
      commit("setLoadingState", { type: "voyageList", value: false });
    } catch (error) {
      commit("setVoyageList", []);
      commit("setErrorState", { type: "voyageList", error });
      commit("setLoadingState", { type: "voyageList", value: false });
    }
  },
  async emailStartedList({ commit }, businessKeys) {
    commit("setEmailDesc", []);
    commit("setBusKey", []);
    try {
      let rawData,
        data = [];

      rawData = await updateEmailStartedTime(businessKeys, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "emailDesc",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }

      commit("setEmailDesc", data);
      commit("setBusKey", businessKeys);
      commit("setErrorState", { type: "emailDesc", error: "" });

      return data;
    } catch (error) {
      commit("setEmailDesc", []);
      commit("setBusKey", []);
      commit("setErrorState", { type: "emailDesc", error });
    }
  },
  async taskStartedList({ commit }, businessKeys) {
    commit("setTaskDesc", []);
    commit("setTskBusKey", []);
    try {
      let rawData,
        data = [];

      rawData = await updateTaskStartedTime(businessKeys, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "taskDesc",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }

      commit("setTaskDesc", data);
      commit("setTskBusKey", businessKeys);
      commit("setErrorState", { type: "taskDesc", error: "" });
    } catch (error) {
      commit("setTaskDesc", []);
      commit("setTskBusKey", []);
      commit("setErrorState", { type: "taskDesc", error });
    }
  },
  async manifestPgSearch({ commit, rootState }) {
    commit("setCompResult", []);
    commit("setStageListValues", []);
    commit("setStatusListValues", []);
    commit("setOkToLoadListValues", []);
    commit("setDeliveryPlacesValues", []);
    commit("setCustomStatusListValues", []);
    commit("setBolTypeListValues", []);
    commit("setBOLSearchNo", "");
    try {
      if (
        state.typeSelected &&
        state.typeSelected != "" &&
        state.countrySelected &&
        state.countrySelected != "" &&
        ((changeFormat(1) &&
          changeFormat(1) != "" &&
          changeFormat(0) &&
          changeFormat(0) != "" &&
          state.deadlineSelected &&
          state.deadlineSelected != "") ||
          (state.vesselCodeSelected && state.vesselVoyageSelected))
      ) {
        commit("setLoadingState", { type: "compResult", value: true });
        let rawData,
          data = [];
        var vesselVoyg = "";
        if (!state.vesselVoyageSelected || state.vesselVoyageSelected === null) {
          vesselVoyg = "";
        } else if (state.vesselVoyageSelected.voyage) {
          vesselVoyg = state.vesselVoyageSelected.voyage;
        } else {
          vesselVoyg = state.vesselVoyageSelected;
        }
        let deleteManifest = false;
        let consignmentType = "BOL";
        if (rootState.commonStore.manifestTypeCountryList && rootState.commonStore.manifestTypeCountryList.length) {
          deleteManifest = getDetletedManifestValue(
            state.countrySelected,
            state.typeSelected,
            rootState.commonStore.manifestTypeCountryList
          );
          consignmentType = getConsignmentValue(
            state.countrySelected,
            state.typeSelected,
            rootState.commonStore.manifestTypeCountryList
          );
        }

        const queryParams = {
          loadPort: state.loadPortSelected ? state.loadPortSelected : "",
          compliancePort: state.compliancePortSelected ? state.compliancePortSelected : "",
          countryCode: state.countrySelected ? state.countrySelected : "",
          complianceType: state.typeSelected ? state.typeSelected : "",
          fromDate: changeFormat(0) ? changeFormat(0) : "",
          toDate: changeFormat(1) ? changeFormat(1) : "",
          dateType: state.deadlineSelected ? state.deadlineSelected : "",
          vesselCode: state.vesselCodeSelected ? state.vesselCodeSelected : "",
          voyage: vesselVoyg ? vesselVoyg : "",
          deleteManifest,
          consignmentType
        };
        // For rail, we are storing clearationLocation in compPort as quick fix
        if (queryParams.complianceType === "Import Rail Manifest") {
          queryParams.clearanceLocation = state.compliancePortSelected || "";
          delete queryParams.compliancePort;
        }
        rawData = await getManifestSearchData(queryParams, true);
        if (rawData.status > 200 && rawData.status <= 299) {
          data = [];
          commit("setErrorState", {
            type: "compResult",
            error: { hasError: false, status: rawData.status, message: rawData.statusText }
          });
        } else {
          data = await rawData.json();
        }
        if (data.length === 0) {
          commit("setErrorState", { type: "compResult", error: { hasError: "Empty Array" } });
        } else {
          commit("setErrorState", { type: "compResult", error: "" });
        }

        commit("setCompResult", data);
        commit("setManifestSearchCurrentPage", 1);
        commit("setLoadingState", { type: "compResult", value: false });
        commit("setPaginationFlag", false);
      }
    } catch (error) {
      commit("setCompResult", []);
      commit("setLoadingState", { type: "compResult", value: false });
      commit("setErrorState", { type: "compResult", error });
    }
  },
  async manifestPgCount({ commit }, req_params) {
    commit("setPgCount", 0);
    try {
      let rawData,
        data = 0;

      const params = {
        compId: req_params.compId,
        loadPort: req_params.loadPort,
        complianceType: req_params.complianceType,
        stageValue:
          state.stageListValues.toString() && state.stageListValues.toString() !== undefined
            ? state.stageListValues.toString()
            : "",
        statusValue:
          state.statusListValues.toString() && state.statusListValues.toString() !== undefined
            ? state.statusListValues.toString()
            : "",
        customsStatus: state.customStatusListValues?.toString(),
        bolType: state.bolTypeListValues?.toString(),
        okToLoadValue: state.okToLoadListValues,
        customNotificationStatus: state.customNotificationStatus ? state.customNotificationStatus : [],
        showExemptionsOnly: state.showExemptionsOnly ? state.showExemptionsOnly : false
      };

      if (params.complianceType === "Import Rail Manifest") {
        params.clearanceLocation = state.compliancePortSelected || "";
      }
      rawData = await getManifestPageCount(params, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "pgCount",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      commit("setPgCount", data);
      commit("setErrorState", { type: "pgCount", error: "" });
    } catch (error) {
      commit("setPgCount", []);
      commit("setErrorState", { type: "pgCount", error });
    }
  },
  async manifestFilterPgCount({ commit }, searchedConsignmentNumber = "") {
    commit("setLoadingState", { type: "consignmentReferenceDetail", value: true });
    try {
      if (state.showExemptionsOnly) {
        commit("setPgCount", {
          bolReferenceCount: Array.isArray(state.complianceDetailsList) ? state.complianceDetailsList.length : 0,
          containerReferenceCount: Array.isArray(state.complianceContainerList)
            ? state.complianceContainerList.length
            : 0,
          consignmentType: state.pgCount?.consignmentType || "BOL"
        });
        commit("setErrorState", { type: "pgCount", error: "" });
        commit("setLoadingState", { type: "consignmentReferenceDetail", value: false });
        return;
      }

      let rawData,
        data = [];
      let queryParams = {
        compId: state.compId,
        loadPort: state.loadPort,
        complianceType: state.complianceType,
        stageValue:
          state.stageListValues.toString() && state.stageListValues.toString() !== undefined
            ? state.stageListValues.toString()
            : "",
        statusValue:
          state.statusListValues.toString() && state.statusListValues.toString() !== undefined
            ? state.statusListValues.toString()
            : "",
        customsStatus: state.customStatusListValues?.toString(),
        bolType: state.bolTypeListValues?.toString(),
        cargoType: getCargoTypeParam(state.cargoTypeListValues),
        moveType: state.moveTypeListValues?.toString(),
        okToLoadValue: state.okToLoadListValues,
        searchedConsignmentNumber: searchedConsignmentNumber
          ? searchedConsignmentNumber
          : state.bolSearchNo
          ? state.bolSearchNo
          : "",
        placeOfDelivery: state.deliveryPlacesValues ? state.deliveryPlacesValues : "",
        customNotificationStatus: state.customNotificationStatus ? state.customNotificationStatus : [],
        showExemptionsOnly: state.showExemptionsOnly ? state.showExemptionsOnly : false
      };
      rawData = await getManifestPageCount(queryParams, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "pgCount",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      commit("setPgCount", data);
      commit("setErrorState", { type: "pgCount", error: "" });
      commit("setLoadingState", { type: "consignmentReferenceDetail", value: false });
    } catch (error) {
      commit("setPgCount", { bolReferenceCount: 0, containerReferenceCount: 0, consignmentType: "BOL" });
      commit("setErrorState", { type: "pgCount", error });
      commit("setLoadingState", { type: "consignmentReferenceDetail", value: false });
    }
  },
  invokeManifestSearchPagination({ commit, dispatch, state }, pageNo) {
    if (pageNo != state.complainceDetailsRecordsPage) {
      commit("setComplianceDetailsList", []);
      commit("setManifestSearchCurrentPage", pageNo);
      commit("setManifestPageSize", constants.pageSettings.manifest.searchResultsPerPage);
      commit("setPaginationFlag", true);
      const newParams = {
        compId: state.compId,
        compPort: state.compPort,
        complianceType: state.typeSelected,
        loadPort: state.loadPort,
        isManuallyTriggered: state.isManuallyTriggered
      };
      dispatch("fetchComplianceDetailsList", newParams);
    }
  },
  invokeManifestSearchPaginationCompact({ commit, dispatch }, params) {
    const { pageNo, pageSize, type } = params;
    let newParams = {
      type: "pagination"
    };
    commit("setComplianceDetailsList", []);
    commit("setPaginationFlag", true);
    commit(
      "setManifestPageSize",
      pageSize > constants.pageSettings.manifest.searchResultsPerPage
        ? pageSize
        : constants.pageSettings.manifest.searchResultsPerPage
    );
    if (type == "number") {
      commit("setManifestSearchCurrentPage", pageNo);
    } else if (type == "size") {
      commit("setManifestSearchCurrentPage", 1);
      dispatch("manifestFilterPgCount");
    }
    dispatch("filterComplianceDetailsList", newParams);
  },
  async manifestBtnSubmission({ commit, dispatch, rootState }, qparams) {
    commit("setManifestSubmission", []);
    commit("setLoadingState", { type: "manifestSub", value: true });
    commit("setIsManuallyTriggered", true);
    try {
      let rawData,
        data = [];

      const { complianceId, firstEventLinkedDate, complianceType, compPort, loadPort, submissionScopes } = qparams;

      const cmpParams = {
        complianceId,
        body: {
          firstEventLinkedDate,
          port: compPort,
          userName: rootState.userAccount.loginDetails.name,
          userId: rootState.userAccount.loginDetails.idTokenClaims.MaerskID,
          submissionScopes,
          isAdmin: rootState.userAccount.loginDetails.idTokenClaims.roles.includes("Manifesting.Admin")
        },
        complianceType,
        loadPort,
        isSelectedAllBol: state.selectedAllBol ? true : false,
        bolNumbers: state.selectedBolList
      };
      rawData = await updateSubmittedManifest(cmpParams, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "manifestSub",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      //Changing the  logic to load both ConsignmentData;
      dispatch("manifestFilterPgCount");
      dispatch("filterComplianceDetailsList", {
        consignmentType: "BOL"
      });
      dispatch("filterComplianceDetailsList", {
        consignmentType: "CONTAINER"
      });
      dispatch("manifestModule/manifestHeaderModule/retrieveNotificationDetails", null, { root: true });
      commit("setManifestSubmission", data);
      commit("setErrorState", { type: "manifestSub", error: "" });
      commit("setLoadingState", { type: "manifestSub", value: false });
    } catch (error) {
      commit("setManifestSubmission", []);
      commit("setErrorState", { type: "manifestSub", error });
      commit("setLoadingState", { type: "manifestSub", value: false });
    }
  },
  async manualSubmission({ commit }, params) {
    commit("setManualStatus", []);
    commit("setLoadingState", { type: "manualSub", value: true });
    try {
      let rawData,
        data = [];
      rawData = await updateComplinaceTaskApproval(params, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "manualSub",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }

      commit("setManualStatus", data);
      commit("setErrorState", { type: "manualSub", error: "" });
      commit("setLoadingState", { type: "manualSub", value: false });
    } catch (error) {
      commit("setManualStatus", []);
      commit("setErrorState", { type: "manualSub", error });
      commit("setLoadingState", { type: "manualSub", value: false });
    }
  },
  async updateVesselReference({ commit, dispatch }, req_body) {
    commit("setManualStatus", []);
    commit("setLoadingState", { type: "updateVessel", value: true });
    try {
      let rawData,
        data = [];
      const cmpParams = {
        gsisKey: state.currentGsisKey,
        req_body
      };
      rawData = await updateVesselReference(cmpParams, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "updateVessel",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }
      const newParams = {
        gsisKey: state.currentGsisKey
      };

      dispatch("fetchVesselDetailsList", newParams);
      commit("setManualStatus", data);
      commit("setErrorState", { type: "updateVessel", error: "" });
      commit("setLoadingState", { type: "updateVessel", value: false });
    } catch (error) {
      commit("setManualStatus", []);
      commit("setErrorState", { type: "updateVessel", error });
      commit("setLoadingState", { type: "updateVessel", value: false });
    }
  },
  async manifestAllRejectedSubmission({ commit }, params) {
    commit("setManualRejSubStatus", []);
    commit("setLoadingState", { type: "manualRejSub", value: true });
    try {
      let rawData,
        data = [];
      rawData = await updateComplinaceRejectedTask(params, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "manualRejSub",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }

      commit("setManualRejSubStatus", data);
      commit("setErrorState", { type: "manualRejSub", error: "" });
      commit("setLoadingState", { type: "manualRejSub", value: false });
    } catch (error) {
      commit("setManualRejSubStatus", []);
      commit("setErrorState", { type: "manualRejSub", error });
      commit("setLoadingState", { type: "manualRejSub", value: false });
    }
  },
  async updateCustomReference({ commit, dispatch }, req_body) {
    commit("setLoadingState", { type: "updateReference", value: true });
    try {
      let rawData;
      rawData = await updateCustomReferences(req_body, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        commit("setErrorState", {
          type: "updateCargo",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      }

      dispatch("filterComplianceDetailsList", {
        consignmentType: "BOL"
      });
      dispatch("filterComplianceDetailsList", {
        consignmentType: "CONTAINER"
      });

      commit("setErrorState", { type: "updateReference", error: "" });
      commit("setLoadingState", { type: "updateReference", value: false });
    } catch (error) {
      commit("setErrorState", { type: "updateReference", error });
      commit("setLoadingState", { type: "updateReference", value: false });
    }
  },
  async manifestRejectedSingleSubmission({ commit }, newParams) {
    commit("setManualRejSingleSubStatus", []);
    commit("setLoadingState", { type: "manualRejSingleSub", value: true });
    try {
      let rawData,
        data = [];
      rawData = await updateComplinaceRejectedTaskByCompliance(newParams, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        data = [];
        commit("setErrorState", {
          type: "manualRejSingleSub",
          error: { hasError: false, status: rawData.status, message: rawData.statusText }
        });
      } else {
        data = await rawData.json();
      }

      commit("setManualRejSingleSubStatus", data);
      commit("setErrorState", { type: "manualRejSingleSub", error: "" });
      commit("setLoadingState", { type: "manualRejSingleSub", value: false });
    } catch (error) {
      commit("setManualRejSingleSubStatus", []);
      commit("setErrorState", { type: "manualRejSingleSub", error });
      commit("setLoadingState", { type: "manualRejSingleSub", value: false });
    }
  },

  async refreshConsignmentData({ rootState }, params) {
    try {
      let rawData;
      //Empty case is not , Offset ans Size are predefined.
      const req_body = {
        complianceId: params.complianceId,
        complianceType: params.complianceType,
        statuses: params.statuses,
        stages: params.stages,
        consignmentType: params.consignmentType,
        offset: "0",
        size: "3000",
        userName: rootState.userAccount.loginDetails.name,
        userId: rootState.userAccount.loginDetails.idTokenClaims.MaerskID,
        empty: params.empty || ""
      };
      rawData = await refreshBol(req_body, true);
      if (rawData.status > 200 && rawData.status <= 299) {
        return false;
      }
      return rawData;
    } catch (error) {
      return false;
    }
  },

  async unlinkManifestAndConsignmentData(_, req_body) {
    let rawData;
    rawData = await unlinkManifestAndConsignment(req_body);
    if (rawData.status > 200 && rawData.status <= 299) {
      return false;
    } else {
      return rawData;
    }
  },

  /**
   * Updates the frozen ETA value for a port call compliance record.
   * This action handles the API call to update frozen estimated time of arrival,
   * with proper error handling and status code validation.
   *
   * @param {Object} _ - Vuex context (unused)
   * @param {Object} req_body - Request payload containing API call parameters
   * @param {Object} req_body.body - The actual data to be sent to the API
   * @param {Object} req_body.headers - Authorization headers including Bearer token
   * @param {string} req_body.urlEndPoint - API endpoint URL for frozen ETA update
   *
   * @returns {Promise<Object>} Resolves with response data on success, rejects with error info on failure
   *
   * @example
   * const req_body = {
   *   body: { complianceId: '123', userId: 'user', userName: 'John', date: '2025-01-01', time: '14:30' },
   *   headers: { Authorization: 'Bearer token', 'consumer-key': 'clientId' },
   *   urlEndPoint: 'https://api.domain.com/frozen-eta'
   * };
   * dispatch('updateFrozenETAValue', req_body).then(result => {...}).catch(error => {...});
   */
  updateFrozenETAValue(_, req_body) {
    return updateFrozenETA(req_body)
      .then(rawData => {
        // Check for successful HTTP status codes (200-299) or updateComplete flag
        if ((rawData.status >= 200 && rawData.status <= 299) || rawData.updateComplete) {
          // Success: return the parsed response data
          return rawData.json
            ? rawData.json().then(responseData => {
                return Promise.resolve(responseData);
              })
            : Promise.resolve(rawData);
        } else {
          // HTTP error: reject with structured error information
          const errorResponse = {
            hasError: true,
            status: rawData.status,
            message: rawData.statusText || "Request failed"
          };
          return Promise.reject(errorResponse);
        }
      })
      .catch(error => {
        // Network or other errors: reject with structured error information
        const errorResponse = {
          hasError: true,
          status: error.status || 500,
          message: error.message || "Network error"
        };
        return Promise.reject(errorResponse);
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
